import React, { useState, useEffect, Suspense, useRef, useCallback } from 'react'
import lazyWithRetry from '../../utils/lazyWithRetry'
import '../../styles/chat/Chat.css'
import { useSocket } from '../../context/socket'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { getMessageList, getMessages, sendMessage, seenMessage, getConversationInfo } from '../../server/server'
import { format_day, translateTargetText, usePageTitle, useQuery } from '../../utils/util'
import { useUser } from '../../auth'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import SkeletonConversation from './skeleton/SkeletonConversation'
import SkeletonChatList from './skeleton/SkeletonChatList'
import ChatStatsModal from './ChatStatsModal'

const Conversation = lazyWithRetry(() => import('./Conversation'), './Conversation')

const ChatListContainer = lazyWithRetry(() => import('./ChatListContainer'), './ChatListContainer')

function Chat() {
    usePageTitle('Upstrive Education - Chat')
    const query = useQuery()
    const history = useHistory()
    const location = useLocation()
    const initConversationId = query.get('conversationId')
    const [messageList, setMessageList] = useState([])
    const { user, setUser } = useUser()
    const { t, locale } = useTranslation()
    const { socketStates, changeSocketState } = useSocket()
    const [conversation, setConversation] = useState({})
    const [selectedConversation, setSelectedConversation] = useState({
        last_message: { seen: [] },
    })

    const [searchTerm, setSearchTerm] = useState('')
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(true)
    const [resultsNum, setResultsNum] = useState(0)
    const [isExpanded, setIsExpanded] = useState(window.innerWidth < 1200)
    const [statsOpen, setStatsOpen] = useState(false)
    // Stable identity so the memoized ChatStatsModal can skip the socket-driven
    // re-renders of this component while it is closed.
    const closeStats = useCallback(() => setStatsOpen(false), [])
    const firstRender = useRef(true)

    const getMessagesList = async () => {
        setLoading(true)

        const response = await getMessageList({
            number_of_loaded: resultsNum,
            search_text: searchTerm,
        })

        const list = response?.data?.list || []
        const size = response?.data?.size || 0

        if (response.status === 200) {
            setMessageList((prev) => {
                const newList = resultsNum === 0 ? list : [...prev, ...list]
                setHasMore(size > newList.length)
                return newList
            })
        } else {
            console.error('Error fetching messages:', response)
            setMessageList([])
            setHasMore(false)
        }

        setLoading(false)
    }

    useEffect(() => {
        getMessagesList()
    }, [resultsNum])

    useEffect(() => {
        if (initConversationId) loadConversationInfo()
    }, [initConversationId])

    const loadConversationInfo = async () => {
        const resp = await getConversationInfo(initConversationId)
        if (resp.status === 200) {
            setSelectedConversation(resp.data)
        }
    }

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false
            return
        }

        if (resultsNum === 0) {
            getMessagesList()
        } else {
            setResultsNum(0)
        }
    }, [searchTerm])
    // useEffect(() => {
    //     getMessagesList();
    //     setLoading(true);
    // }, [socketStates.lastMessage]);
    const getConversationStatus = (conversation) => {
        const receivedMessage = user.id !== conversation.last_message?.user_id
        const isNewMessage = !conversation?.last_message?.seen?.includes(user.id) && conversation?.last_message?.user_id != user.id
        const status = receivedMessage ? (isNewMessage ? i18n.t('newMessage') : i18n.t('received')) : i18n.t('delivered')
        return status
    }
    // useEffect(() => {
    //     return () => {
    //         changeSocketState({ openedConversation: null });
    //     };
    // }, []);
    const checkUnseenConversations = (conversation_id) => {
        const index = socketStates?.unseenConversations?.findIndex((e) => e === conversation_id)
        let _socket = { ...socketStates }
        _socket['openedConversation'] = conversation_id
        if (index > -1) {
            const ids = socketStates?.unseenConversations?.filter((e) => e !== conversation_id)
            console.log('Changing unseen conversations: ', ids)
            _socket['unseenConversations'] = ids
        }
        changeSocketState(_socket)
    }
    const handleSeen = async (conversation_id, last_message) => {
        if (!!last_message && !last_message?.seen?.includes(user.id) && last_message?.user_id !== user.id) {
            const response = await seenMessage({
                message_body: last_message?.message_body,
                conversation_id: conversation_id,
                user_id: last_message?.user_id,
                seen: [...last_message?.seen, user.id],
                created_date: last_message?.created_date,
                id: last_message?.message_id,
            })
            if (response.status === 200) {
                console.log('ok')
                getMessagesList()
            }
        }
    }
    useEffect(() => {
        if (selectedConversation?.conversation_id) {
            handleSeen(selectedConversation?.conversation_id, selectedConversation?.last_message)
            changeSocketState({
                openedConversation: selectedConversation?.conversation_id,
            })
            checkUnseenConversations(selectedConversation?.conversation_id)
        }
    }, [selectedConversation])

    const handleItemSelect = (item) => {
        setSelectedConversation(item)
        history.replace({
            pathname: location.pathname,
            search: '', // Clears the query parameters
        })
        if (window.innerWidth < 1200 && isExpanded) {
            setIsExpanded(false)
        }
    }

    const handleExpand = () => {
        if (window.innerWidth < 1200 && isExpanded === false) {
            setIsExpanded(true)
        }
    }
    const initials = (string) => {
        const initial = string
            .split(' ')
            .map((i) => i.charAt(0).toUpperCase())
            .slice(0, 2)
        return initial.join('')
    }

    return (
        <div className="chat-home-container">
            <div className={`chat-container ${i18n.language == 'ar' ? 'ar-lang' : ''}`}>
                <Suspense fallback={<SkeletonChatList />}>
                    <ChatListContainer
                        messageList={messageList}
                        setSearchTerm={setSearchTerm}
                        selectedConversation={selectedConversation}
                        setResultsNum={setResultsNum}
                        hasMore={hasMore}
                        handleItemSelect={handleItemSelect}
                        getConversationStatus={getConversationStatus}
                        loading={loading}
                        handleExpand={handleExpand}
                        onOpenStats={() => setStatsOpen(true)}
                    />
                </Suspense>
                {selectedConversation?.conversation_id && isExpanded && <div className="click-chat" onClick={() => setIsExpanded(false)}></div>}

                {!!selectedConversation?.conversation_id && (
                    <Suspense fallback={<SkeletonConversation />}>
                        <Conversation conversation={selectedConversation} user={user} />
                    </Suspense>
                )}
            </div>
            <ChatStatsModal isOpen={statsOpen} onClose={closeStats} />
        </div>
    )
}

export default Chat
